import { MenuIcon, MenuItem, MenuText, Tooltip } from "pixel";
import { Link, LinkProps, useLocation } from "react-router-dom";
import RenderByProbeServiceConfig from "../packages/accessControl/RenderByProbeServiceConfig";
import { getDefaultHealthMetricsDateRange } from "../packages/components/functional/Metrics/HealthMetrics/utils";
import { useUserInfo } from "../packages/context/userinfo/useUserInfo";
import useUIState from "../packages/hooks/useUIState";
import { FP_CONSOLE_HOME_NAV_ROLES } from "../packages/utils/constants";
import { ConditionalRenderer } from "../packages/utils/helpers";
import { ProtectedMenu } from "../packages/utils/routeHelpers";
import { isAuthorisedRole } from "../packages/utils/utils";
import { matchCurrentPath } from "./utils";

type FpConsoleNavLinkProps = LinkProps & {
  children: React.ReactNode;
};

const FpConsoleNavLink = ({ children, onClick, ...rest }: FpConsoleNavLinkProps) => {
  const { handleToggleMobileMenu } = useUIState();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    handleToggleMobileMenu(true);
    onClick?.(e);
  };

  return (
    <Link onClick={handleClick} {...rest}>
      {children}
    </Link>
  );
};

const generateHealthMetricsUrl = () => {
  const { start_date, end_date } = getDefaultHealthMetricsDateRange();

  const searchParams = new URLSearchParams({
    tenant: "all",
    start_date: start_date,
    end_date: end_date,
  });

  return `/fp-console/health-metrics?${searchParams.toString()}`;
};

interface FpConsoleNavItemsProps {
  isSideBarCollapsed: boolean;
}
const FpConsoleNavItems = ({ isSideBarCollapsed }: FpConsoleNavItemsProps) => {
  const location = useLocation();
  const userInfo = useUserInfo();
  const isPathActive = (requiredRoute: string) => matchCurrentPath(location, requiredRoute);
  const canAccessHome = isAuthorisedRole(FP_CONSOLE_HOME_NAV_ROLES, userInfo?.data.userInfo?.roles);

  return (
    <>
      <div>
        <ConditionalRenderer check={canAccessHome}>
          <MenuItem customClass="mb-4">
            <div className="collapse-menu-title">Home</div>
            <FpConsoleNavLink className="block" to="/">
              <Tooltip
                placement="right"
                content="Home"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <MenuIcon customClass="block">Home</MenuIcon>
              </Tooltip>

              <MenuText>Home</MenuText>
            </FpConsoleNavLink>
          </MenuItem>
        </ConditionalRenderer>

        <ProtectedMenu matchRoute="/fp-console/order-insights">
          <RenderByProbeServiceConfig requiredProbeServices={["oms", "warehouse"]}>
            <MenuItem customClass="mb-4">
              <FpConsoleNavLink className="block" to="/fp-console/order-insights">
                <Tooltip
                  placement="right"
                  content="Order insights"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <MenuIcon active={isPathActive("/fp-console/order-insights")}>Metrics</MenuIcon>
                </Tooltip>

                <MenuText active={isPathActive("/fp-console/order-insights")}>
                  Order insights
                </MenuText>
              </FpConsoleNavLink>
            </MenuItem>
          </RenderByProbeServiceConfig>
        </ProtectedMenu>

        <ProtectedMenu matchRoute="/fp-console/kyc-insights">
          <RenderByProbeServiceConfig requiredProbeServices={["kyc_verification", "oms"]}>
            <MenuItem customClass="mb-4">
              <FpConsoleNavLink className="block" to="/fp-console/kyc-insights">
                <Tooltip
                  placement="right"
                  content="KYC insights"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <MenuIcon active={isPathActive("/fp-console/kyc-insights")}>
                    ContactOutlined
                  </MenuIcon>
                </Tooltip>

                <MenuText active={isPathActive("/fp-console/kyc-insights")}>KYC insights</MenuText>
              </FpConsoleNavLink>
            </MenuItem>
          </RenderByProbeServiceConfig>
        </ProtectedMenu>

        <ProtectedMenu matchRoute="/fp-console/health-metrics">
          <RenderByProbeServiceConfig requiredProbeServices={["warehouse"]}>
            <MenuItem customClass="mb-4">
              <FpConsoleNavLink className="block w-full" to={generateHealthMetricsUrl()}>
                <Tooltip
                  placement="right"
                  content="Health metrics"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <MenuIcon active={isPathActive("/fp-console/health-metrics")}>Metrics</MenuIcon>
                </Tooltip>

                <MenuText active={isPathActive("/fp-console/health-metrics")}>
                  Health metrics
                </MenuText>
              </FpConsoleNavLink>
            </MenuItem>
          </RenderByProbeServiceConfig>
        </ProtectedMenu>

        <ProtectedMenu matchRoute="/fp-console/action-center">
          <MenuItem customClass="mb-4">
            <div className="collapse-menu-title">Action center</div>
            <FpConsoleNavLink className="flex items-center" to="/fp-console/action-center">
              <Tooltip
                placement="right"
                content="Action center"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <MenuIcon customClass="block" active={isPathActive("/fp-console/action-center")}>
                  Action
                </MenuIcon>
              </Tooltip>

              <MenuText active={isPathActive("/fp-console/action-center")}>Action center</MenuText>
            </FpConsoleNavLink>
          </MenuItem>
        </ProtectedMenu>

        <ProtectedMenu matchRoute="/fp-console/order-failure-code-list">
          <MenuItem customClass="mb-4">
            <div className="collapse-menu-title">Failure codes</div>
            <FpConsoleNavLink className="block" to="/fp-console/order-failure-code-list">
              <Tooltip
                placement="right"
                content="Failure codes"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <MenuIcon active={isPathActive("/fp-console/order-failure-code-list")}>
                  FailureCode
                </MenuIcon>
              </Tooltip>
              <MenuText active={isPathActive("/fp-console/order-failure-code-list")}>
                Failure codes
              </MenuText>
            </FpConsoleNavLink>
          </MenuItem>
        </ProtectedMenu>
      </div>

      <ProtectedMenu matchRoute="/fp-console/fp-account-details">
        <>
          <ProtectedMenu matchRoute="/fp-console/fp-account-details/tenants" matchExact={true}>
            <MenuItem customClass="mb-4">
              <div className="collapse-menu-title">Tenants</div>
              <FpConsoleNavLink className="block" to="/fp-console/fp-account-details/tenants">
                <Tooltip
                  placement="right"
                  content="FP accounts"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <MenuIcon active={isPathActive("/fp-console/fp-account-details/tenants")}>
                    Tenant
                  </MenuIcon>
                </Tooltip>

                <MenuText active={isPathActive("/fp-console/fp-account-details/tenants")}>
                  FP accounts
                </MenuText>
              </FpConsoleNavLink>
            </MenuItem>
          </ProtectedMenu>

          <ProtectedMenu
            matchRoute="/fp-console/fp-account-details/user-management"
            matchExact={true}
          >
            <MenuItem customClass="mb-4">
              <div className="collapse-menu-title">Tenants</div>
              <FpConsoleNavLink
                className="block"
                to="/fp-console/fp-account-details/user-management"
              >
                <Tooltip
                  placement="right"
                  content="User management"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <MenuIcon active={isPathActive("/fp-console/fp-account-details/user-management")}>
                    UserFilled
                  </MenuIcon>
                </Tooltip>

                <MenuText active={isPathActive("/fp-console/fp-account-details/user-management")}>
                  User management
                </MenuText>
              </FpConsoleNavLink>
            </MenuItem>
          </ProtectedMenu>
        </>
      </ProtectedMenu>

      <ProtectedMenu matchRoute="/fp-console/pincodes">
        <>
          <MenuItem customClass="mb-4">
            <div className="collapse-menu-title">Tenants</div>
            <FpConsoleNavLink className="block" to="/fp-console/pincodes">
              <Tooltip
                placement="right"
                content="Pincodes"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <MenuIcon active={isPathActive("/fp-console/pincodes")}>PIN</MenuIcon>
              </Tooltip>

              <MenuText active={isPathActive("/fp-console/pincodes")}>Pincodes</MenuText>
            </FpConsoleNavLink>
          </MenuItem>

          <MenuItem customClass="mb-4">
            <div className="collapse-menu-title">Tenants</div>
            <FpConsoleNavLink className="block" to="/fp-console/ifsc-codes">
              <Tooltip
                placement="right"
                content="IFSC codes"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <MenuIcon active={isPathActive("/fp-console/ifsc-codes")}>IFSC</MenuIcon>
              </Tooltip>

              <MenuText active={isPathActive("/fp-console/ifsc-codes")}>IFSC codes</MenuText>
            </FpConsoleNavLink>
          </MenuItem>
        </>
      </ProtectedMenu>

      <ProtectedMenu matchRoute="/fp-console/scheme-master-management">
        <MenuItem customClass="mb-4">
          <FpConsoleNavLink className="block" to="/fp-console/scheme-master-management">
            <Tooltip
              placement="right"
              content="Scheme master management"
              hidden={!isSideBarCollapsed}
              customClass="block"
            >
              <MenuIcon active={isPathActive("/fp-console/scheme-master-management")}>
                Operation
              </MenuIcon>
            </Tooltip>

            <MenuText active={isPathActive("/fp-console/scheme-master-management")}>
              Scheme master management
            </MenuText>
          </FpConsoleNavLink>
        </MenuItem>
      </ProtectedMenu>
    </>
  );
};
export default FpConsoleNavItems;
